import { useEffect, useRef, useState } from "react";
import { LiaSearchPlusSolid, LiaSearchSolid } from "react-icons/lia";
import { MdClose } from "react-icons/md";
import { HiOutlinePlus } from "react-icons/hi";
import Button from "../../common/Button";
import ErrorModal from "../../common/CustomModel";
import ConfirmDeleteModal from "../../common/DeleteModel";
import FormFields from "../../common/FormFields";
import Pagination from "../../common/Pagination";
import Model from "../../common/Model";
import Loader from "../../common/Loader";
import Table from "../../common/Table";
import { UsersAPI } from "../../utlis/apiService";
import { notify } from "../../common/Toast";
import { RegistryHeader, Button as SharedButton } from "../../common/Shared";

const Users = () => {
  const user = JSON.parse(sessionStorage.getItem("user"));
  const userId = user?.userId;
  const [searchQuery, setSearchQuery] = useState("");
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorSubMessage, setErrorSubMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({
    user_Id: 0,
    userName: "",
    encryptedPassword: "",
  });
  const [hasMore, setHasMore] = useState(true);
  const isFetchingRef = useRef(false);
  const tableScrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [isUserData, setIsUserData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  const fetchUserData = async () => {
    if (isFetchingRef.current) return;
    setLoading(true);
    try {
      const res = await UsersAPI.getAllPagination(currentPage, pageSize);
      const newItems = res.data;

      if (currentPage === 1) {
        setIsUserData(newItems);
      } else {
        setIsUserData((prev) => [...prev, ...newItems]);
      }

      if (newItems.length < pageSize) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    } catch (error) {
      notify("Error fetching location data", "error");
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;

    const onScroll = () => {
      if (loading || !hasMore || isFetchingRef.current) return;

      const { scrollTop, clientHeight, scrollHeight } = el;

      if (scrollHeight - scrollTop <= clientHeight + 100) {
        isFetchingRef.current = true;
        setCurrentPage((p) => p + 1);
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [loading, hasMore]);

  useEffect(() => {
    fetchUserData();
  }, [currentPage]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setIsUserData(isUserData);
    } else {
      const filtered = isUserData.filter((d) =>
        d.userName.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setIsUserData(filtered);
    }
  }, [searchQuery, isUserData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdd = () => {
    setIsModelOpen(true);
    setIsEditing(false);
    setFormData({
      user_Id: 0,
      userName: "",
      encryptPassword: "",
    });
  };

  const handleEdit = async (id) => {
    try {
      const data = await UsersAPI.getById(id);
      if (data) {
        setFormData({
          user_Id: data.user_Id,
          userName: data.userName,
          encryptedPassword: data.encryptedPassword,
        });
        setIsEditing(true);
        setIsModelOpen(true);
      }
    } catch (error) {
      notify("Failed to fetch location details", "error");
    }
  };

  const handleDelete = (id) => {
    setSelectedId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await UsersAPI.delete(selectedId);
      notify("Deleted successfully", "success");
      fetchUserData();
    } catch (error) {
      notify("Failed to delete record", "error");
    }
    setIsDeleteModalOpen(false);
    setSelectedId(null);
  };

  const handleSubmit = async ({ resetOnly = false }) => {
    try {
      await UsersAPI.saveOrUpdate(formData);
      notify(
        isEditing ? "Updated successfully" : "Added successfully",
        "success",
      );
      fetchUserData();
      setFormData({
        user_Id: 0,
        userName: "",
        encryptPassword: "",
      });
      setIsEditing(false);
      if (!resetOnly) {
        setIsModelOpen(false);
      }
    } catch (error) {
      notify("Failed to submit form", "error");
      setErrorMessage("Submission failed");
      setErrorSubMessage("Please check your input or try again.");
      setErrorModalOpen(true);
    }
  };

  const fields = [
    {
      name: "userName",
      label: "User Name",
      type: "text",
      required: true,
    },
    {
      name: "encryptedPassword",
      label: "Password",
      type: "text",
      required: true,
    },
  ];

  return (
    <>
      <div className="flex flex-col h-full bg-white font-plus">
        <RegistryHeader title="Users Master">
          <div className="relative w-full sm:w-80 group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none group-focus-within:text-[#3F80F7]">
              <LiaSearchSolid size={18} className="text-gray-400 transition-colors" />
            </div>
            <input
              type="text"
              placeholder="Search..."
              className="block w-full pl-12 pr-10 py-3 bg-gray-50 border border-gray-200 text-gray-900 text-[11px] font-black uppercase tracking-[2px] focus:border-[#3F80F7] focus:bg-white focus:outline-none transition-all placeholder:text-gray-300"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-red-500 transition-colors"
              >
                <MdClose size={18} />
              </button>
            )}
          </div>

          <SharedButton
            onClick={handleAdd}
            icon={HiOutlinePlus}
          >
            Add User
          </SharedButton>
        </RegistryHeader>

        <div className="px-8 pt-0 pb-4 flex-1 overflow-hidden flex flex-col">
          <div
            ref={tableScrollRef}
            className="relative overflow-auto border border-gray-200 bg-white flex-1"
          >
            {loading && <Loader centered />}
            <div className="min-w-max">
              <Table
                columns={[
                  { key: "user_Id", label: "Id" },
                  { key: "userName", label: "User Name" },
                ]}
                data={isUserData}
                onEdit={(row) => handleEdit(row.user_Id)}
                onDelete={(row) => handleDelete(row.user_Id)}
                tableClassName="text-[12px] font-bold"
              />
            </div>
          </div>
        </div>
      </div>

      <Model
        isOpen={isModelOpen}
        onClose={() => setIsModelOpen(false)}
        name={isEditing ? "Edit User" : "Add User"}
      >
        <FormFields
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit({ resetOnly: false });
          }}
          onSubmitAndAddNew={(e) => {
            e.preventDefault();
            handleSubmit({ resetOnly: true });
          }}
          setIsModelOpen={setIsModelOpen}
          formData={formData}
          handleInputChange={handleInputChange}
          fields={fields}
          isEditing={isEditing}
          onClear={() => setIsModelOpen(false)}
        />
      </Model>

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
      />

      {errorModalOpen && (
        <ErrorModal
          message={errorMessage}
          subMessage={errorSubMessage}
          onClose={() => setErrorModalOpen(false)}
        />
      )}
    </>
  );
};

export default Users;
